/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import HeadTittle from "../../../../../../components/HeadTittle";
import DivContainerBody from "../../../../../../components/DivContainerBody";
import ButtonAction from "../../../../../../components/buttons/ButtonAction";
import {EditIcon, IconDocumentWarning, IconVehicleInMotion} from "../../../../../../components/buttons/IconButtons";
import {permissionObj} from "../../../../../../Helpers/permission";
import routes from "../../../../../../Routes/routes";
import EntryAndExitFormDetailsSectionInfo
    from "../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/EntryAndExitFormDetailsSectionInfo";
import {getVehicleMovementDetailsSections} from "../../../../MaterialControl/Model/Model";
import GuardGateLogSection from "../../EntryAndExitForms/Details/Sections/GuardGateLogSection";
import {
    getGateLogNotePlaceholder,
    showsGuardGateMovementLog,
    showsGuardTripRecord
} from "../../EntryAndExitForms/Details/Model/Model";
import StatusBadgeSection
    from "../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/StatusBadgeSection";
import DiscrepancySection
    from "../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/DiscrepancySection";
import ReportDispatchModal from "../../EntryAndExitForms/Details/Modal/ReportDispatchModal";
import GuardVehicleMovementGateLogSection, {getGuardVehicleFields}
    from "./Sections/GuardVehicleMovementGateLogSection";
import LivePoultryTruckTransportSection
    from "../../../../MaterialControl/SubComponents/EntryAndExitForms/LivePoultryTruck/LivePoultryTruckTransportSection";
import GuardVehicleMovementDetailsApp from "./Functionality/GuardVehicleMovementDetailsApp";

/**
 * Detail of a movement of a vehicle read by the guard at the gate.
 *
 * The information of the folio is the one the requester registered, so the guard
 * only reads it: while the folio waits for the vehicle it registers the arrival
 * with its notes and its evidence, and once the vehicle is at the gate it
 * completes the vehicle that leaves and the moment the movement happened.
 *
 * @returns {JSX.Element} Detail of the movement with the gate log of the guard.
 */
const GuardVehicleMovementDetails = () => {
    const {
        t,
        navigate,
        DEV,
        isGuard,
        permission,
        item,
        badgeItem,
        evidence,
        statusId,
        gateLog,
        handleChange,
        photoPicker,
        setPhotoPicker,
        emptyFields,
        isClosed,
        showBadge,
        hasDiscrepancies,
        discrepancyFiles,
        directionOption,
        showTransport,
        showTransportRecord,
        containerCardRef,
        contentCardRef,
        containerTableRef,
        contentTableRef,
        showConfirmModal,
        handleDispatch,
        openReportModal,
        handleActionModal,
        closeModal,
        goBack
    } = GuardVehicleMovementDetailsApp();

    if (!DEV && !isGuard &&
        !permission?.includes(permissionObj.WEB_VEHICLE_MOVEMENTS_READ)
    )
        navigate(routes.forbidden);

    // A movement reported with discrepancies has to be reviewed before the gate
    // writes anything else on it, so it is only read until the report is attended
    const canClose = (DEV || isGuard)
        && !isClosed
        && !hasDiscrepancies;

    // The gate writes the log of the movement the folio declared — the arrival
    // of the vehicle or its departure — and keeps reading it once that movement
    // was registered, so it is read the same way every form reads it.
    const showsGateMovementLog = showsGuardGateMovementLog(item);

    // A movement dispatched without requiring the arrival or the departure of the
    // vehicle does not repeat the entry or exit of the trip in its information
    const showTripRecord = showsGuardTripRecord(item);

    // The log is written for the movement the folio waits for, so the notes ask
    // for what is seen when the vehicle comes in or for what is seen when it
    // leaves the plant
    const notePlaceholder = getGateLogNotePlaceholder({
        statusId: statusId,
        movementDirection: item?.movementDirection
    });

    return (
        <>
            <HeadTittle name={item?.code || 'details'} onBack={goBack}>
                {canClose &&
                    <>
                        <ButtonAction
                            id='btn_gvm_dispatch'
                            className='btn-icon-edit'
                            Icon={EditIcon}
                            name='dispatch'
                            onClick={() => handleDispatch()}
                        />
                        <ButtonAction
                            id='btn_gvm_report'
                            className='btn-icon-delete'
                            Icon={IconDocumentWarning}
                            name='report'
                            onClick={() => openReportModal()}
                        />
                    </>
                }
            </HeadTittle>
            <DivContainerBody>
                <div className={"mb-4"}>
                    {/* The gate is the one that registered the movement, so it is
                        not told back that it was dispatched; the report it has
                        to attend is the only result read at the top */}
                    <StatusBadgeSection
                        t={t}
                        item={badgeItem}
                        showBadge={showBadge && hasDiscrepancies}
                    />
                </div>
                <div className='row px-0'>
                    <div className='col-sm-12 col-md-4 col-lg-4 mb-lg-0 mb-md-3 mb-sm-3 pl-0 pr-sm-0 pr-md-3'
                         ref={containerCardRef}>
                        {/* The gate writes the movement in its own card, and
                        the record of the folio is what says when it happened
                        and who registered it, so the movement of the vehicle is
                        read in the card of the request as well */}
                        <EntryAndExitFormDetailsSectionInfo
                            item={item}
                            evidence={evidence}
                            sections={getVehicleMovementDetailsSections(item)}
                            statusId={statusId}
                            movementType={directionOption}
                            contentCardRef={contentCardRef}
                            showTripRecord={showTripRecord}
                        />
                    </div>
                    <div className='col-sm-12 col-md-8 col-lg-8 px-0' ref={containerTableRef}>
                        {hasDiscrepancies &&
                            <div className={"mb-3"}>
                                <DiscrepancySection
                                    t={t}
                                    reviewedBy={badgeItem?.dispatchUser}
                                    reviewedAt={badgeItem?.dispatchDate}
                                    reason={badgeItem?.dispatchReason}
                                    files={discrepancyFiles}
                                />
                            </div>
                        }

                        {/* The movement the gate already registered is read with
                        the log it was written with, the same one the folio that
                        still waits for the vehicle is captured with. */}
                        {showsGateMovementLog ?
                            <GuardGateLogSection
                                t={t}
                                gateLog={gateLog}
                                handleChange={handleChange}
                                photoPicker={photoPicker}
                                setPhotoPicker={setPhotoPicker}
                                emptyFields={emptyFields}
                                disabled={isClosed || hasDiscrepancies}
                                notePlaceholder={notePlaceholder}
                                contentRef={contentTableRef}
                                // The vehicle is read in the moment it reaches
                                // the gate, so the log of that movement opens
                                // with the unit it was read with
                                transportSection={(showTransport || showTransportRecord) &&
                                    <LivePoultryTruckTransportSection
                                        t={t}
                                        materialRequest={gateLog}
                                        handleChange={handleChange}
                                        emptyFields={emptyFields}
                                        fields={getGuardVehicleFields(
                                            item,
                                            isClosed || hasDiscrepancies || !showTransport
                                        )}
                                        disabled={isClosed || hasDiscrepancies || !showTransport}
                                        contentClassName={'row input-padding-top pb-3'}
                                        card={false}
                                    />
                                }
                            />
                            :
                            <div className={"overflow-y-auto"} ref={contentTableRef}>
                                <GuardVehicleMovementGateLogSection
                                    t={t}
                                    item={item}
                                    gateLog={gateLog}
                                    handleChange={handleChange}
                                    directionOption={directionOption}
                                    emptyFields={emptyFields}
                                    disabled={isClosed || hasDiscrepancies}
                                    showTransport={showTransport || showTransportRecord}
                                    transportDisabled={!showTransport}
                                />

                                <div className={"mt-3"}>
                                    <GuardGateLogSection
                                        t={t}
                                        gateLog={gateLog}
                                        handleChange={handleChange}
                                        photoPicker={photoPicker}
                                        setPhotoPicker={setPhotoPicker}
                                        emptyFields={emptyFields}
                                        disabled={isClosed || hasDiscrepancies}
                                        title={'dispatch_details'}
                                        evidenceCard={false}
                                        icon={IconVehicleInMotion}
                                        noteName={'dispatchNote'}
                                        noteTrans={'dispatch_note'}
                                        notePlaceholder={notePlaceholder}
                                    />
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </DivContainerBody>

            {showConfirmModal &&
                <ReportDispatchModal
                    show={showConfirmModal}
                    onHide={closeModal}
                    onConfirm={(comment, files) => handleActionModal(comment, files)}
                    title_modal={'report_dispatch'}
                    description={t('are_you_sure_to_proceed')}
                    subDescription={t('please_provide_the_required_information_below')}
                />
            }
        </>
    );
};

export default GuardVehicleMovementDetails;
